import * as Sentry from "@sentry/react"

/**
 * Initialize Sentry for error tracking and performance monitoring
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE

  // Skip initialization if DSN is not configured (local development)
  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking disabled.")
    return
  }

  Sentry.init({
    dsn,
    environment,

    // Set sample rate based on environment
    // Production: 100% of errors, 10% of transactions
    // Staging: 100% of errors, 50% of transactions
    // Development: Don't send (DSN should not be set)
    tracesSampleRate: environment === "production" ? 0.1 : 0.5,

    // Capture 100% of errors
    sampleRate: 1.0,

    // Replay sample rates (must be at root; not in replayIntegration options)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Enable performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Don't send events in development (extra safety)
    enabled: environment !== "development",

    // Filter sensitive data
    beforeSend(event, _hint) {
      // Remove sensitive query params
      if (event.request?.url) {
        const url = new URL(event.request.url)
        // Remove potential sensitive params
        url.searchParams.delete("token")
        url.searchParams.delete("api_key")
        url.searchParams.delete("stripe_key")
        event.request.url = url.toString()
      }

      // Don't send localhost errors in production (shouldn't happen but just in case)
      if (event.request?.url?.includes("localhost")) {
        return null
      }

      return event
    },

    // Ignore known errors that aren't actionable
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "chrome-extension://",
      "moz-extension://",
      // Network errors (user's internet connection)
      "Network request failed",
      "Failed to fetch",
      // ResizeObserver loop errors (benign)
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
  })
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(user: { id: string; email?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  })
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
  Sentry.setUser(null)
}

/**
 * Manually capture an error
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level?: "info" | "warning" | "error"
) {
  Sentry.addBreadcrumb({
    message,
    category: category || "app",
    level: level || "info",
  })
}
